'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdAdd, MdMedication, MdSearch, MdClose } from 'react-icons/md'
import {
    getPrescriptions,
    HospitalPrescription,
    PRESCRIPTION_STATUS_LABELS,
    PRESCRIPTION_STATUS_COLORS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function PrescriptionsPage() {
    const navigate = useNavigate()
    const [prescriptions, setPrescriptions] = useState<HospitalPrescription[]>(
        []
    )
    const [filteredPrescriptions, setFilteredPrescriptions] = useState<
        HospitalPrescription[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadPrescriptions()
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = prescriptions.filter(
                p =>
                    p.admission?.client.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    p.prescriptionNumber
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredPrescriptions(filtered)
        } else {
            setFilteredPrescriptions(prescriptions)
        }
    }, [searchTerm, prescriptions])

    const loadPrescriptions = async () => {
        setLoading(true)
        try {
            const response = await getPrescriptions(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setPrescriptions(response.result)
                setFilteredPrescriptions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar prescrições')
            }
        } catch (error) {
            toast.error('Erro ao carregar prescrições')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando prescrições...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdMedication className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Prescrições Hospitalares
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie prescrições e checagem de medicamentos
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/prescricoes/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Nova</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente ou número..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredPrescriptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMedication className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma prescrição encontrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece criando uma nova prescrição
                        </p>
                        <button
                            onClick={() =>
                                navigate('/hospital/prescricoes/new')
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-xl" />
                            Nova prescrição
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Número</th>
                                <th>Leito</th>
                                <th>Validade</th>
                                <th>Médico</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPrescriptions.map(prescription => (
                                <tr
                                    key={prescription.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/prescricoes/${prescription.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {
                                                prescription.admission?.client
                                                    .name
                                            }
                                        </div>
                                    </td>
                                    <td>{prescription.prescriptionNumber}</td>
                                    <td>
                                        {prescription.admission?.bed?.number ||
                                            '-'}
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {new Date(
                                                prescription.validFrom
                                            ).toLocaleDateString('pt-BR')}
                                            {' - '}
                                            {new Date(
                                                prescription.validUntil
                                            ).toLocaleDateString('pt-BR')}
                                        </div>
                                    </td>
                                    <td>
                                        {prescription.physician?.name || '-'}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${PRESCRIPTION_STATUS_COLORS[prescription.status]}`}
                                        >
                                            {
                                                PRESCRIPTION_STATUS_LABELS[
                                                    prescription.status
                                                ]
                                            }
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
